import { z } from "zod";
import { defineEndpoint, idParamsSchema } from "../endpoint.js";
import { appVersionSchema, deviceSyncHealthSchema } from "./health.js";
/**
 * POS devices, pairing and staff PINs (docs/specs/pos-sync.md "Device identity and security", B-45). A manager
 * creates a one-time code for a register in Admin; the POS exchanges it once for its long-lived credential.
 */
export const DEVICE_PLATFORM_VALUES = ["windows", "macos", "linux"];
/** Crockford Base32 without I, L, O, U, shown and typed as `XXXX-XXXX` (case and the dash are ignored). */
export const PAIRING_CODE_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
export const pairingCodeSchema = z
    .string()
    .trim()
    .transform((value) => value.toUpperCase().replace(/[\s-]/g, ""))
    .pipe(z.string().regex(/^[0-9A-HJKMNP-TV-Z]{8}$/));
/** A 4–6 digit POS PIN (PRD §6.4). */
export const posPinSchema = z.string().regex(/^\d{4,6}$/);
export const deviceSchema = z.object({
    id: z.uuid(),
    name: z.string(),
    platform: z.enum(DEVICE_PLATFORM_VALUES),
    locationId: z.uuid(),
    registerId: z.uuid(),
    /** `P<n>`, part of every bill number of this device (`B1-P2-000123`); never reused at the location. */
    deviceCode: z.string(),
    pairedAt: z.iso.datetime(),
    revokedAt: z.iso.datetime().nullable(),
    version: z.int(),
});
/** A device with its sync health (B-51): null until its first push or heartbeat. */
export const deviceDetailSchema = deviceSchema.extend({ sync: deviceSyncHealthSchema.nullable() });
export const deviceListSchema = z.object({
    data: z.array(deviceDetailSchema),
    nextCursor: z.uuid().nullable(),
});
/** `GET /v1/devices?locationId=&status=&limit=&cursor=` (newest first). */
export const deviceQuerySchema = z.object({
    locationId: z.uuid().optional(),
    status: z.enum(["active", "revoked"]).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    cursor: z.uuid().optional(),
});
/** `POST /v1/devices/pairing-codes`: the device name is chosen here, by the manager. */
export const createPairingCodeRequestSchema = z.object({
    registerId: z.uuid(),
    name: z.string().trim().min(1).max(100),
});
export const pairingCodeResponseSchema = z.object({
    /** Shown once, as `XXXX-XXXX`; only its hash is kept. */
    code: z.string(),
    expiresAt: z.iso.datetime(),
    locationId: z.uuid(),
    registerId: z.uuid(),
});
/** `POST /v1/pos/pair`: sent by the POS app. */
export const pairDeviceRequestSchema = z.object({
    code: pairingCodeSchema,
    platform: z.enum(DEVICE_PLATFORM_VALUES),
    appVersion: appVersionSchema,
});
export const pairedDeviceSchema = z.object({
    /** `<tenantId>.<deviceId>.<secret>`: returned once; the POS keeps it in the OS keychain. */
    credential: z.string(),
    tenantId: z.uuid(),
    device: deviceSchema,
});
/** `PUT /v1/me/pos-pin`: the caller's own PIN in the current tenant. */
export const setPosPinRequestSchema = z.object({ pin: posPinSchema });
/** `GET /v1/me/pos-pin`: whether the caller has a PIN in the current tenant. */
export const posPinStatusSchema = z.object({ isSet: z.boolean(), updatedAt: z.iso.datetime().nullable() });
// Endpoints ----------------------------------------------------------------------------------------------------------
const tag = { tag: "pos-devices" };
const view = { permission: "devices.view" };
const manage = { permission: "devices.manage" };
export const listDevices = defineEndpoint({
    ...tag,
    name: "listDevices",
    method: "GET",
    path: "/v1/devices",
    access: view,
    summary: "POS devices at the caller's locations, newest first",
    query: deviceQuerySchema,
    status: 200,
    response: deviceListSchema,
});
export const getDevice = defineEndpoint({
    ...tag,
    name: "getDevice",
    method: "GET",
    path: "/v1/devices/{id}",
    access: view,
    summary: "One POS device with its sync health",
    params: idParamsSchema,
    status: 200,
    response: deviceDetailSchema,
});
export const createPairingCode = defineEndpoint({
    ...tag,
    name: "createPairingCode",
    method: "POST",
    path: "/v1/devices/pairing-codes",
    access: manage,
    summary: "A one-time code (10 minutes) that pairs a POS with this register",
    body: createPairingCodeRequestSchema,
    status: 201,
    response: pairingCodeResponseSchema,
    errors: ["REGISTER_HAS_DEVICE", "PLAN_LIMIT_REACHED"],
    entitlement: "devices.max",
});
export const revokeDevice = defineEndpoint({
    ...tag,
    name: "revokeDevice",
    method: "POST",
    path: "/v1/devices/{id}/revoke",
    access: manage,
    summary: "Revoke a device: its tokens stop working and it locks on next contact (data kept)",
    params: idParamsSchema,
    status: 200,
    response: deviceSchema,
});
export const pairDevice = defineEndpoint({
    ...tag,
    name: "pairDevice",
    method: "POST",
    path: "/v1/pos/pair",
    access: { public: true },
    summary: "Exchange a pairing code for the device credential (once); wrong or used code → 404",
    body: pairDeviceRequestSchema,
    status: 201,
    response: pairedDeviceSchema,
    errors: ["REGISTER_HAS_DEVICE", "RATE_LIMITED", "PLAN_LIMIT_REACHED"],
    entitlement: "devices.max",
});
export const getPosPin = defineEndpoint({
    ...tag,
    name: "getPosPin",
    method: "GET",
    path: "/v1/me/pos-pin",
    access: { signedIn: true },
    summary: "Whether the caller has a POS PIN in this business",
    status: 200,
    response: posPinStatusSchema,
});
export const setPosPin = defineEndpoint({
    ...tag,
    name: "setPosPin",
    method: "PUT",
    path: "/v1/me/pos-pin",
    access: { signedIn: true },
    summary: "Set the caller's POS PIN in this business (4–6 digits)",
    body: setPosPinRequestSchema,
    status: 204,
});
export const resetPosPin = defineEndpoint({
    ...tag,
    name: "resetPosPin",
    method: "POST",
    path: "/v1/users/{id}/pos-pin/reset",
    access: manage,
    summary: "Remove a staff member's POS PIN and lockouts; they set a new one",
    params: idParamsSchema,
    status: 204,
});
//# sourceMappingURL=devices.js.map